/**
 * @file Defines types for state shared by the Class Search page
 */
import type { ArtistDataItem } from '@engage/api-v2/generated/../../api-v2-models/generated/ArtistDataItem';
import type { ClassPreview } from '@engage/library';
import type { MetadataSelectorState } from '@engage/metadata';

/**
 * These are the statuses that the Class Search page can be in. The page can only be in one state at a time.
 */
export type Status =
  | 'preload' // Before the metadata mappings have been loaded.
  | 'preloaded' // Before query params have been parsed.
  | 'initial' // Ready for user interaction.
  | 'searching' // UI is requesting a search to happen.
  | 'waitingForSearch' // XHR requests for artists or classes have started (and both have not completed).
  | 'showResults' // There are either artists or classes to show.
  | 'noResults' // Both XHR requests returned no results.
  | 'loadingArtist' // UI is requesting to view an artist.
  | 'viewingArtist' // Classes XHR request completed and there are classes to show.
  | 'backToClasses'; // UI is requesting to exit Class Search. See `packages/@members/pg-search/context/useBackToClasses.ts`.

/**
 * The type of search being performed. Mainly used for analytics.
 */
export enum SearchType {
  /**
   * User manually entered the search.
   */
  ManualSearch = 0,
  /**
   * User clicked a popular query.
   */
  PopularQuery,
}

export type State = {
  /**
   * The status the Class Search page is in.
   */
  status: Status;

  /**
   * The current search query.
   */
  query?: string;

  /**
   * The id of the artist for the artist page
   */
  artistId?: string;

  /**
   * A copy of metadata mappings (pulled from redux) for generating class previews.
   * See `packages/@members/pg-search/context/useMetadataMappings.ts`.
   */
  metadata?: MetadataSelectorState['entities'];

  classes?: ClassPreview[];
  artists?: ArtistDataItem[];

  /**
   * Records what browse category Class Search was launched from (or undefined if launched from classes page).
   * This is used to return the user to the right page when clicking back.
   */
  fromCategory?: string;

  /**
   * Storing the search type for analytics.
   */
  searchType?: SearchType;

  /**
   * Storing which position in the artist results an artist was in when it was clicked (for analytics).
   */
  artistListPosition?: number;

  trackedAnalytics: boolean;

  queryCounter: number;

  previousPage?: string;
};

/**
 * Type to show when status is being set when it shouldn't. We don't allow `action` to change `status` since that
 * violates the rule that only transitions can change `status`.
 */
export type MutableState = Omit<State, 'status'> & { status: undefined };

export const nextQueryCounter = (queryCounter: number): number => {
  const nextValue = new Date().valueOf() + Math.round(Math.random() * 1000);

  if (nextValue === queryCounter) {
    return nextQueryCounter(queryCounter);
  }

  return nextValue;
};

export const InitialState: Omit<State, 'status'> = {
  query: '',
  artistId: undefined,
  artistListPosition: undefined,
  artists: undefined,
  classes: undefined,
  searchType: undefined,
  trackedAnalytics: false,
  queryCounter: nextQueryCounter(0),
};

/**
 * This is the State needed when going from viewing artists back to search.
 */
export const BackToSearchState: Partial<MutableState> = {
  artistId: undefined,
  artistListPosition: undefined,
  artists: undefined,
  classes: undefined,
};

/**
 * These are status where searching (XHR requests) are *not* allowed.
 */
export const InitialStatuses: Status[] = ['preload', 'preloaded'];

/**
 * These are status where we're waiting for a search to return.
 */
export const SearchingStatuses: Status[] = [
  'searching',
  'waitingForSearch',
  'loadingArtist',
];
